import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { PenTool, ArrowRight, Check } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import useSeo from "../lib/useSeo";

export default function ApplyPage() {
    useSeo({
        title: "Apply to Publish",
        description:
            "Apply to publish with EIGOO Books. Share a sample, pick your genre, keep your IP.",
    });
    const [params] = useSearchParams();
    const defaultTier = params.get("tier") || "";
    const [form, setForm] = useState({
        name: "",
        email: "",
        genre: "",
        sample: "",
        note: defaultTier ? `Interested in the ${defaultTier} tier.` : "",
        website: "", // honeypot — must stay empty, only bots fill it
    });
    const [state, setState] = useState({ type: "idle", msg: "" });

    const submit = async (e) => {
        e.preventDefault();
        setState({ type: "loading", msg: "" });
        try {
            await api.post("/publishing/apply", form);
            setState({
                type: "ok",
                msg: "Your submission is in the editor's queue. We'll be in touch within 5 business days.",
            });
            setForm({ name: "", email: "", genre: "", sample: "", note: "", website: "" });
        } catch (err) {
            setState({
                type: "err",
                msg:
                    formatApiErrorDetail(err?.response?.data?.detail) ||
                    err?.message ||
                    "Something went wrong.",
            });
        }
    };

    return (
        <main
            data-testid="apply-page"
            className="max-w-5xl mx-auto px-6 lg:px-10 py-20"
        >
            <div className="text-center mb-14">
                <div className="eyebrow mb-3 inline-flex items-center gap-2">
                    <PenTool className="w-3.5 h-3.5" /> Writer application
                </div>
                <h1 className="font-serif text-5xl md:text-7xl leading-[0.98] tracking-tight">
                    Tell us about your{" "}
                    <span className="text-gold-gradient italic">next book</span>.
                </h1>
                <p className="text-[#A1A1AA] mt-6 max-w-xl mx-auto">
                    Share a sample and a sentence of ambition. We read every application. We
                    respond to each within 5 business days.
                </p>
            </div>

            <div className="grid md:grid-cols-12 gap-10">
                <motion.form
                    onSubmit={submit}
                    data-testid="apply-form"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="md:col-span-7 frame-gold p-8 md:p-10 space-y-5"
                >
                    <Field label="Your name">
                        <input
                            data-testid="apply-name"
                            required
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            className="w-full bg-[#141414] border px-4 py-3 text-white"
                            style={{ borderColor: "rgba(212,175,55,0.2)" }}
                        />
                    </Field>
                    <Field label="Email">
                        <input
                            data-testid="apply-email"
                            required
                            type="email"
                            value={form.email}
                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                            className="w-full bg-[#141414] border px-4 py-3 text-white"
                            style={{ borderColor: "rgba(212,175,55,0.2)" }}
                        />
                    </Field>
                    <Field label="Writing genre">
                        <input
                            data-testid="apply-genre"
                            required
                            placeholder="Literary Fiction, Sci-Fi, Comic Series, Memoir…"
                            value={form.genre}
                            onChange={(e) => setForm({ ...form, genre: e.target.value })}
                            className="w-full bg-[#141414] border px-4 py-3 text-white"
                            style={{ borderColor: "rgba(212,175,55,0.2)" }}
                        />
                    </Field>
                    <Field label="Sample (first 300–500 words is perfect)">
                        <textarea
                            data-testid="apply-sample"
                            required
                            rows={8}
                            value={form.sample}
                            onChange={(e) => setForm({ ...form, sample: e.target.value })}
                            className="w-full bg-[#141414] border px-4 py-3 text-white resize-y"
                            style={{ borderColor: "rgba(212,175,55,0.2)" }}
                        />
                    </Field>
                    <Field label="Anything else? (tier interest, deadlines, links)">
                        <textarea
                            data-testid="apply-note"
                            rows={3}
                            value={form.note}
                            onChange={(e) => setForm({ ...form, note: e.target.value })}
                            className="w-full bg-[#141414] border px-4 py-3 text-white resize-y"
                            style={{ borderColor: "rgba(212,175,55,0.2)" }}
                        />
                    </Field>

                    {/* Honeypot — hidden from humans, bots will fill it and be dropped silently. */}
                    <div
                        aria-hidden="true"
                        style={{
                            position: "absolute",
                            left: "-10000px",
                            top: "auto",
                            width: "1px",
                            height: "1px",
                            overflow: "hidden",
                        }}
                    >
                        <label htmlFor="website">Website (leave empty)</label>
                        <input
                            id="website"
                            name="website"
                            type="text"
                            tabIndex={-1}
                            autoComplete="off"
                            value={form.website}
                            onChange={(e) => setForm({ ...form, website: e.target.value })}
                        />
                    </div>

                    <button
                        type="submit"
                        data-testid="apply-submit"
                        disabled={state.type === "loading"}
                        className="btn-gold w-full justify-center disabled:opacity-50"
                    >
                        {state.type === "loading" ? "Submitting…" : "Submit application"}
                        <ArrowRight className="w-3.5 h-3.5" />
                    </button>

                    {state.msg && (
                        <p
                            data-testid="apply-status"
                            className={`text-xs uppercase tracking-widest ${
                                state.type === "err"
                                    ? "text-[#cd6a6a]"
                                    : "text-[#D4AF37]"
                            }`}
                        >
                            {state.msg}
                        </p>
                    )}
                </motion.form>

                <aside className="md:col-span-5">
                    <div className="frame-gold p-8">
                        <div className="eyebrow mb-4">What you get</div>
                        <ul className="space-y-3 text-sm text-[#cfcfcf]">
                            {[
                                "A human read of your sample within 5 business days",
                                "Clear tier recommendation (Starter, Pro, Premier)",
                                "Your IP stays yours — no rights grab",
                                "Clear 50–60% royalty on every sale",
                                "A direct contact at the imprint",
                            ].map((b) => (
                                <li key={b} className="flex items-start gap-2">
                                    <Check className="w-4 h-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                                    <span>{b}</span>
                                </li>
                            ))}
                        </ul>
                        <div className="gold-rule my-6" />
                        <p className="text-[#A1A1AA] text-xs leading-relaxed">
                            Already applied? Keep writing. Our editors will follow up at the email
                            you submitted. Check your spam folder — our dispatches sometimes land there.
                        </p>
                        <Link
                            to="/publish#tiers"
                            className="btn-ghost-gold mt-6 inline-flex !py-2 !px-4 text-[10px]"
                        >
                            Compare tiers <ArrowRight className="w-3 h-3" />
                        </Link>
                    </div>
                </aside>
            </div>
        </main>
    );
}

function Field({ label, children }) {
    return (
        <div className="space-y-1.5">
            <label className="eyebrow text-[10px] block">{label}</label>
            {children}
        </div>
    );
}
